import React, { useState } from 'react';
import { Upload, Form } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '../../../../hooks/useToastMessages.jsx';

const { Dragger } = Upload;

const isAcceptedFile = (file, accept) => {
  if (!accept) {
    return true;
  }

  const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
  const acceptedTypes = accept
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean);

  return acceptedTypes.some((type) => {
    if (type.startsWith('.')) {
      return type === fileExtension;
    }

    if (type.endsWith('/*')) {
      return file.type.toLowerCase().startsWith(type.slice(0, -1));
    }

    return file.type.toLowerCase() === type;
  });
};

const isWithinMaxSize = (file, maxSize) =>
  !maxSize || file.size <= maxSize;

// https://4x.ant.design/components/upload/
const FormFileUpload = ({
  label,
  name,
  rules,
  fileName = undefined,
  onPreview,
  hintText,
  accept,
  maxSize,
  ...rest
}) => {
  return (
    <Form.Item label={label} name={name} rules={rules}>
      <FileUploadWrapper
        fileName={fileName}
        onPreview={onPreview}
        hintText={hintText}
        accept={accept}
        maxSize={maxSize}
        {...rest}
      />
    </Form.Item>
  );
};

// value and onChange props are used to bind to the values of this form item.
const FileUploadWrapper = ({
  value,
  onChange,
  disabled,
  fileName = undefined,
  onPreview,
  hintText,
  accept,
  maxSize,
  ...rest
}) => {
  const { t } = useTranslation();
  const { errorMessage } = useToastMessages();
  // In case of the uploaded image, server returns just the relative URL path,
  // so convert it to the object antd can understand.
  const [fileList, setFileList] = useState(
    value
      ? [
          {
            name: fileName ?? value,
            url: value,
            status: 'done',
          },
        ]
      : [],
  );

  // When uploading image, image is uploaded with the form data as File object.
  // Ants is used to upload the image via ajax, then get the URL and submit that URL with the form data.
  // So some hacing is applied here based on: https://levelup.gitconnected.com/managing-file-uploads-with-ant-design-6d78e592f2c4
  const props = {
    fileList,
    customRequest: ({ _, onSuccess }) => setTimeout(() => onSuccess(), 0), // This is required so Antd does not upload the image ittself.
    beforeUpload: (file) => {
      if (!isAcceptedFile(file, accept)) {
        errorMessage({
          message: t(`Nepovolený typ súboru. Povolené formáty: ${accept}`),
        });

        return Upload.LIST_IGNORE;
      }

      if (!isWithinMaxSize(file, maxSize)) {
        errorMessage({
          message: t(
            `Súbor je príliš veľký. Maximálna veľkosť je ${Math.round(maxSize / 1024 / 1024)} MB.`,
          ),
        });

        return Upload.LIST_IGNORE;
      }

      setFileList([file]);

      if (onChange) {
        onChange(file);
      }

      return false; // Return false here so Antd uploading is not triggered.
    },
    onRemove: (file) => {
      const filtered = fileList.filter((f) => f.name !== file.name);
      setFileList(filtered);

      if (onChange) {
        onChange(filtered.length > 0 ? filtered[0] : null);
      }

      return false;
    },
    ...(onPreview && { onPreview }),
  };

  return (
    <Dragger disabled={disabled} accept={accept} {...props} {...rest}>
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">
        {t('Kliknite alebo potiahnite súbor sem na nahratie')}
      </p>
      {hintText && (
        <p
          className="ant-upload-hint"
          style={{ fontSize: '10px', color: '#888' }}
        >
          {hintText}
        </p>
      )}
    </Dragger>
  );
};

export default FormFileUpload;
